import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  InlineError,
  Button,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";

const AddEditTask = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id, taskId } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    title: "",
    status: "",
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/goals/${id}/tasks/${taskId}`)
      .then(() => {
        navigate(`/admin/goals/${id}/tasks`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    const requiredFields = ["title", "status"];
    const newErrors = {};
    let hasError = false;

    requiredFields.forEach((field) => {
      if (!item[field]) {
        hasError = true;
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/goals/${id}/tasks/${taskId}`)
        .then((result) => {
          const data = result?.data?.data || {};
          setItem({
            title: data?.title || "", // fallback if API still returns "task"
            status: data?.status ? String(data?.status.id) : "",
          });
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  const handleSave = () => {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");
    formData.append("title", item.title);
    formData.append("status", item.status);

    axios
      .post(
        mode === "new" ? `/goals/${id}/tasks` : `/goals/${id}/tasks/${taskId}`,
        formData
      )
      .then(() => {
        mode === "new" ? navigate(`/admin/goals/${id}/tasks`) : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          setError(error.response.data.message);
        }
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Subtask`}
      backAction={{ onAction: () => navigate(`/admin/goals/${id}/tasks`) }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <TextField
            label="Title"
            value={item.title}
            onChange={(value) => handleChange("title", value)}
            error={errors.title}
          />

          <Select
            label="Status"
            options={[
              { label: "Pending", value: "1" },
              { label: "In Progress", value: "2" },
              { label: "Done", value: "3" },
            ]}
            value={item.status}
            onChange={(value) => handleChange("status", value)}
            error={errors.status}
            placeholder="Please select"
          />

          <InlineError message={error} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          { content: "No", onAction: toggleDeleteModal, loading: isSaving },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
    </Page>
  );
};

export default AddEditTask;
